/**
 * Create/Edit case form dialog
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@mui/material';
import { Case, CaseCreate, CaseUpdate } from '../types/case';
import apiService from '../services/api';
import { isMspAgreementServiceType } from '../utils/mspAgreement';
import { isoUtcToDatetimeLocalValue, localDatetimeInputToIsoUtc } from '../utils/datetimeLocal';

type OptionItem = { value: string; label: string; sort_order: number };

const DEFAULT_STATUS: OptionItem[] = [
  { value: 'new', label: 'New', sort_order: 10 },
  { value: 'in_progress', label: 'In Progress', sort_order: 20 },
  { value: 'awaiting_end_user', label: 'Awaiting End User', sort_order: 30 },
  { value: 'awaiting_upstream_carrier', label: 'Awaiting Upstream Carrier', sort_order: 40 },
  { value: 'awaiting_nbn_appointment', label: 'Awaiting NBN Appointment', sort_order: 50 },
  { value: 'awaiting_supa_appointment', label: 'Awaiting Supa Appointment', sort_order: 60 },
  { value: 'escalated', label: 'Escalated', sort_order: 70 },
  { value: 'on_hold', label: 'On Hold', sort_order: 80 },
  { value: 'billing_system_update_required', label: 'Billing System Update Required', sort_order: 90 },
  { value: 'completed', label: 'Completed', sort_order: 100 },
];
const DEFAULT_CASE_REASON: OptionItem[] = [
  { value: 'sonic_internet_nbn', label: 'Sonic Internet - NBN', sort_order: 10 },
  { value: 'sonic_internet_supa', label: 'Sonic Internet - Supa', sort_order: 20 },
  { value: 'sonic_mobile_optus', label: 'Sonic Mobile - Optus', sort_order: 30 },
  { value: 'sonic_voice_vhv', label: 'Sonic Voice (VHV)', sort_order: 40 },
  { value: 'sonic_web_hosting', label: 'Sonic Web Hosting', sort_order: 50 },
];
const DEFAULT_PRIORITY: OptionItem[] = [
  { value: 'low', label: 'Low', sort_order: 10 },
  { value: 'medium', label: 'Medium', sort_order: 20 },
  { value: 'high', label: 'High', sort_order: 30 },
];
const DEFAULT_ORIGIN: OptionItem[] = [
  { value: 'phone', label: 'Phone', sort_order: 10 },
  { value: 'sms', label: 'SMS', sort_order: 20 },
  { value: 'email', label: 'Email', sort_order: 30 },
  { value: 'web', label: 'Web', sort_order: 40 },
  { value: 'staff', label: 'Staff', sort_order: 50 },
];

interface CaseFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  accountId?: string;
  caseData?: CaseCreate | Case | null;
  mode?: 'create' | 'edit';
  caseId?: string;
}

const CaseFormDialog: React.FC<CaseFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  accountId,
  caseData,
  mode = 'create',
  caseId,
}) => {
  const [form, setForm] = useState<Partial<CaseCreate> & { msp_use_hours?: boolean; msp_hours_input?: string }>({
    account_id: accountId || '',
    subject: '',
    description: '',
    case_type: 'service',
    case_reason: '',
    case_origin: 'phone',
    contact_id: '',
    account_service_id: '',
    account_asset_id: '',
    status: 'new',
    priority: 'medium',
    assignee_id: '',
    due_by: '',
    msp_use_hours: false,
    msp_hours_input: '',
  });
  const [accounts, setAccounts] = useState<{ id: string; display_name: string }[]>([]);
  const [services, setServices] = useState<
    { id: string; service_id: string; service_type: string; service_type_name?: string | null }[]
  >([]);
  const [mspBalanceHint, setMspBalanceHint] = useState<string | null>(null);
  const [assets, setAssets] = useState<{ id: string; asset_name: string; model_number?: string | null }[]>([]);
  const [users, setUsers] = useState<{ id: string; username: string; display_name: string }[]>([]);
  const [fieldOptions, setFieldOptions] = useState<{
    status: OptionItem[];
    case_reason: OptionItem[];
    priority: OptionItem[];
    case_origin: OptionItem[];
  }>({ status: [], case_reason: [], priority: [], case_origin: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const desc = caseData?.description;
    setForm({
      account_id: accountId || (caseData as { account_id?: string })?.account_id || form.account_id || '',
      subject: caseData?.subject || '',
      description: desc === null || desc === undefined ? '' : desc,
      case_type: caseData?.case_type || 'service',
      case_reason: caseData?.case_reason || '',
      case_origin: caseData?.case_origin || 'phone',
      contact_id: caseData?.contact_id || '',
      account_service_id: caseData?.account_service_id || '',
      account_asset_id: (caseData as Case)?.account_asset_id || '',
      status: caseData?.status || 'new',
      priority: caseData?.priority || 'medium',
      assignee_id: caseData?.assignee_id || '',
      due_by: isoUtcToDatetimeLocalValue(caseData?.due_by),
      msp_use_hours: !!(caseData as Case)?.msp_use_hours,
      msp_hours_input: (caseData as Case)?.msp_hours_reserved || '',
    });
    setError(null);
    apiService.getUsers().then(setUsers).catch(() => {});
    apiService.getAccounts({ per_page: 200 }).then((r: any) => {
      setAccounts(r.accounts || r || []);
    }).catch(() => {});
    apiService.getCaseFieldOptionsGrouped().then(setFieldOptions).catch(() => {});
  }, [open, accountId, caseData]);

  useEffect(() => {
    if (!form.account_id) {
      setServices([]);
      setAssets([]);
      return;
    }
    apiService.getAccountServices(form.account_id).then((r: any) => {
      setServices(r.services || r || []);
    }).catch(() => setServices([]));
    apiService.getAccountAssets(form.account_id).then((r: any) => {
      setAssets(r.assets || r || []);
    }).catch(() => setAssets([]));
  }, [form.account_id]);

  useEffect(() => {
    if (!form.account_service_id) {
      setMspBalanceHint(null);
      return;
    }
    const s = services.find((x) => x.id === form.account_service_id);
    if (!s || !isMspAgreementServiceType(s.service_type, s.service_type_name)) {
      setMspBalanceHint(null);
      return;
    }
    let cancelled = false;
    apiService
      .getMspBalance(s.id)
      .then((b: any) => {
        if (!cancelled) {
          setMspBalanceHint(
            `MSP balance — available: ${b.available_total} h (granted ${b.total_grants}, reserved ${b.reserved_total})`
          );
        }
      })
      .catch(() => {
        if (!cancelled) setMspBalanceHint(null);
      });
    return () => {
      cancelled = true;
    };
  }, [form.account_service_id, services]);

  const handleSubmit = async () => {
    if (!form.subject?.trim()) {
      setError('Subject is required');
      return;
    }
    if (mode === 'create' && !form.account_id) {
      setError('Account is required');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (mode === 'edit' && caseId) {
        const payload: CaseUpdate = {
          subject: form.subject,
          description: form.description || undefined,
          case_type: form.case_type,
          case_reason: form.case_reason || undefined,
          case_origin: form.case_origin,
          contact_id: form.contact_id || undefined,
          account_service_id: form.account_service_id || undefined,
          account_asset_id: form.account_asset_id || undefined,
          status: form.status,
          priority: form.priority,
          assignee_id: form.assignee_id || undefined,
          due_by: localDatetimeInputToIsoUtc(form.due_by as string),
          msp_use_hours: !!form.msp_use_hours,
          msp_hours: form.msp_use_hours ? parseFloat(form.msp_hours_input || '0') : undefined,
        };
        await apiService.updateCase(caseId, payload);
      } else {
        const payload: CaseCreate = {
          account_id: form.account_id!,
          subject: form.subject!,
          description: form.description || undefined,
          case_type: form.case_type!,
          case_reason: form.case_reason || undefined,
          case_origin: form.case_origin!,
          contact_id: form.contact_id || undefined,
          account_service_id: form.account_service_id || undefined,
          account_asset_id: form.account_asset_id || undefined,
          status: form.status!,
          priority: form.priority!,
          assignee_id: form.assignee_id || undefined,
          due_by: localDatetimeInputToIsoUtc(form.due_by as string),
          msp_use_hours: !!form.msp_use_hours,
          msp_hours: form.msp_use_hours ? parseFloat(form.msp_hours_input || '0') : undefined,
        };
        await apiService.createCase(payload);
      }
      onSuccess();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to save case');
    } finally {
      setLoading(false);
    }
  };

  const selectedAccount = accounts.find((a) => a.id === form.account_id);
  const statusOptions = fieldOptions.status.length > 0 ? fieldOptions.status : DEFAULT_STATUS;
  const caseReasonOptions = fieldOptions.case_reason.length > 0 ? fieldOptions.case_reason : DEFAULT_CASE_REASON;
  const priorityOptions = fieldOptions.priority.length > 0 ? fieldOptions.priority : DEFAULT_PRIORITY;
  const originOptions = fieldOptions.case_origin.length > 0 ? fieldOptions.case_origin : DEFAULT_ORIGIN;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === 'edit' ? 'Edit Case' : 'Add Case'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {mode === 'create' && (
            <Autocomplete
              options={accounts}
              getOptionLabel={(a) => (a as any).display_name || (a as any).organisation_name || a.id}
              value={selectedAccount || null}
              onChange={(_, v) =>
                setForm({ ...form, account_id: (v as any)?.id || '', account_service_id: '', account_asset_id: '', msp_use_hours: false, msp_hours_input: '' })
              }
              renderInput={(params) => <TextField {...params} label="Account" required />}
              disabled={!!accountId}
            />
          )}
          <TextField
            label="Subject"
            required
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            fullWidth
          />
          <TextField
            label="Description"
            multiline
            rows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={form.case_type}
              label="Type"
              onChange={(e) => setForm({ ...form, case_type: e.target.value as any })}
            >
              <MenuItem value="service">Service</MenuItem>
              <MenuItem value="billing">Billing</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Case Reason</InputLabel>
            <Select
              value={form.case_reason || ''}
              label="Case Reason"
              onChange={(e) => setForm({ ...form, case_reason: (e.target.value as string) || undefined })}
            >
              <MenuItem value="">None</MenuItem>
              {caseReasonOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Origin</InputLabel>
            <Select
              value={form.case_origin}
              label="Origin"
              onChange={(e) => setForm({ ...form, case_origin: e.target.value as string })}
            >
              {originOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {services.length > 0 && (
            <FormControl fullWidth>
              <InputLabel>Related service</InputLabel>
              <Select
                value={form.account_service_id || ''}
                label="Related service"
                onChange={(e) => setForm({ ...form, account_service_id: e.target.value || undefined })}
              >
                <MenuItem value="">None</MenuItem>
                {services.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.service_id} ({s.service_type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {form.account_service_id &&
            (() => {
              const s = services.find((x) => x.id === form.account_service_id);
              if (!s || !isMspAgreementServiceType(s.service_type, s.service_type_name)) return null;
              return (
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                  {mspBalanceHint && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {mspBalanceHint}
                    </Typography>
                  )}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!form.msp_use_hours}
                        onChange={(e) => setForm({ ...form, msp_use_hours: e.target.checked })}
                      />
                    }
                    label="Use MSP agreement hours for this case"
                  />
                  {form.msp_use_hours && (
                    <TextField
                      label="Hours to allocate"
                      type="number"
                      inputProps={{ step: 0.25, min: 0 }}
                      value={form.msp_hours_input || ''}
                      onChange={(e) => setForm({ ...form, msp_hours_input: e.target.value })}
                      fullWidth
                      sx={{ mt: 1 }}
                      required
                    />
                  )}
                </Box>
              );
            })()}
          {assets.length > 0 && (
            <FormControl fullWidth>
              <InputLabel>Linked asset</InputLabel>
              <Select
                value={form.account_asset_id || ''}
                label="Linked asset"
                onChange={(e) => setForm({ ...form, account_asset_id: e.target.value || undefined })}
              >
                <MenuItem value="">None</MenuItem>
                {assets.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.asset_name}
                    {a.model_number ? ` (${a.model_number})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={form.status}
              label="Status"
              onChange={(e) => setForm({ ...form, status: e.target.value as string })}
            >
              {statusOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              value={form.priority}
              label="Priority"
              onChange={(e) => setForm({ ...form, priority: e.target.value as string })}
            >
              {priorityOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Assignee</InputLabel>
            <Select
              value={form.assignee_id || ''}
              label="Assignee"
              onChange={(e) => setForm({ ...form, assignee_id: e.target.value || undefined })}
            >
              <MenuItem value="">Unassigned</MenuItem>
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.display_name || u.username}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Due by"
            type="datetime-local"
            value={form.due_by ? (form.due_by as string).slice(0, 16) : ''}
            onChange={(e) => setForm({ ...form, due_by: e.target.value || undefined })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : mode === 'edit' ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CaseFormDialog;
